import { v4 as uuidv4 } from "uuid";
import {
  Logger,
  shallowCopy,
  Graph,
  ImageNode,
  isUndefined,
  Matrix2DFields,
  identityMatrix2DFields,
  Matrix2D,
} from "@noli/core";
import { CloudCacheType, CloudProjectType, CurrentCloudProject } from "./types/cloud";
import { DEFAULT_PROJECT_PREVIEW, localCacheKeyMap, USER_ID_CACHE_KEY } from "./constant";
import { BoardStore, langStore, useIsReady } from "@noli/business";
import { cloudStorage } from "@/utils/storage";
import { LocalCacheType } from "./types/local";
import { formatTime } from "@/utils/format-time";
import { loginStore } from "../login/store";
import { localCacheManager } from "./localManager";
import { isString } from "lodash";

export const DEFAULT_PROJECT_CONFIG = {
  projectId: () => getNewProjectId(),
  projectName: () => getDefaultProjectName(),
  graph: () => new Graph(),
  globalTransform: () => Object.assign({}, identityMatrix2DFields) as Matrix2DFields,
  localCache: () => {
    const defaultCache: LocalCacheType = {
      id: DEFAULT_PROJECT_CONFIG.projectId(),
      name: DEFAULT_PROJECT_CONFIG.projectName(),
      graph: DEFAULT_PROJECT_CONFIG.graph(),
      globalTransform: DEFAULT_PROJECT_CONFIG.globalTransform(),
      userId: loginStore.userInfo.userId ?? undefined,
      updateTime: new Date().getTime(),
    };
    return defaultCache;
  },
};

function getNewProjectId(): string {
  return uuidv4();
}

function getDefaultProjectName(): string {
  const updateTime = new Date().getTime().toString();
  const prefix = langStore.tgt === "zh" ? "项目" : "Work";
  return `${prefix} ${formatTime(parseInt(updateTime), "YYYY-MM-DD HH:mm").slice(2)}`;
  // return langStore.tgt === "zh" ? "默认项目" : "Default Work";
}

export const localCacheValueFormatter: Record<keyof LocalCacheType, (v: any) => string | void> = {
  id: (id): string => id ?? DEFAULT_PROJECT_CONFIG.projectId().toString(),
  name: (name): string => name ?? DEFAULT_PROJECT_CONFIG.projectName(),
  graph: (graph: Graph): string => graph.toJson(),
  globalTransform: (globalTransform: Matrix2DFields): string => JSON.stringify(globalTransform),
  updateTime: (updateTime: number): string => updateTime.toString(),
  userId: (userId: number | undefined): void => {
    if (isUndefined(userId)) {
      cloudStorage.remove(localCacheKeyMap.userId);
    } else {
      cloudStorage.save(localCacheKeyMap.userId, userId.toString());
    }
  },
  commit_id: (commitId?: string): void => {
    if (isUndefined(commitId) || !commitId) {
      cloudStorage.remove(localCacheKeyMap.commit_id);
    } else {
      cloudStorage.save(localCacheKeyMap.commit_id, commitId);
    }
  },
  preview: (preview = ""): void => {
    cloudStorage.save(localCacheKeyMap.preview, preview || "");
  },
};

export const localCacheValueGetter: Record<keyof LocalCacheType, (v: any) => any> = {
  id: (id): string => {
    if (!id) {
      id = DEFAULT_PROJECT_CONFIG.projectId();
      cloudStorage.save(localCacheKeyMap.id, id);
    }
    return id;
  },
  name: (name): string => {
    if (!name) {
      name = DEFAULT_PROJECT_CONFIG.projectName();
      cloudStorage.save(localCacheKeyMap.name, name);
    }
    return name;
  },
  graph: (graphJsonString: string) => {
    if (!graphJsonString) {
      const newGraph = new Graph();
      cloudStorage.save(localCacheKeyMap.graph, newGraph.toJson());
      return newGraph;
    }
    return Graph.fromJson(graphJsonString).clean().graph;
  },
  globalTransform: (transformJsonString?: string): Matrix2DFields => {
    if (!transformJsonString) return identityMatrix2DFields;
    return Matrix2D.fromJson(transformJsonString);
  },
  updateTime: (updateTime?: string): number => {
    if (!updateTime) {
      updateTime = Date.now().toString();
      cloudStorage.save(localCacheKeyMap.updateTime, updateTime);
    }
    return parseInt(updateTime);
  },
  userId: (userId?: string): number | undefined => {
    return isString(userId) ? parseInt(userId) : undefined;
  },
  commit_id: (commitId?: string): string | undefined => {
    return commitId ?? undefined;
  },
  preview: (preview = "") => preview,
};

export function updateLocalUserId(localCache: LocalCacheType, userId: number): void {
  if (isUndefined(localCache.userId)) {
    localCache.userId = userId;
    cloudStorage.save(USER_ID_CACHE_KEY, userId.toString());
  }
}

export async function loadJson<T = any>(source: string): Promise<T | null> {
  const isUrl = /^http?s/i.test(source);
  if (isUrl) {
    return fetch(source).then((res) => res.json());
  } else {
    try {
      return JSON.parse(source);
    } catch {
      return null;
    }
  }
}

export const ProjectTransformer = {
  localToCloud: transformLocalToCloudJson,
  cloudToLocal: transformCloudJsonToLocal,
  localToCurrent: transformLocalToCurrentCloud,
};

function transformLocalToCloudJson(localCache: LocalCacheType): CloudCacheType {
  return {
    graphInfo: shallowCopy(localCache.graph.toJsonInfo()),
    globalTransform: shallowCopy(localCache.globalTransform),
  };
}

async function transformCloudJsonToLocal(project: CloudProjectType): Promise<LocalCacheType> {
  const cloudCacheJson = project.url || project.content;
  Logger.debug(`>> detected : ${project.project_name}`);
  const cloudCache = await loadJson(cloudCacheJson);
  if (!cloudCache.globalTransform) throw Error("no globalTransform");
  // 云上的更新时间是按秒记的，所以乘上 1000 换算成毫秒
  const cloudUpdateTime = project.update_time * 1000;
  return {
    id: project.project_id,
    name: project.project_name,
    graph: Graph.fromJsonInfo(cloudCache.graphInfo),
    globalTransform: cloudCache.globalTransform,
    updateTime: cloudUpdateTime,
    userId: loginStore.userInfo.userId,
    commit_id: project.commit_id,
  };
}

function transformLocalToCurrentCloud(localCache: LocalCacheType): CurrentCloudProject {
  const currentData: CurrentCloudProject = {
    project_id: localCache.id,
    project_name: localCache.name,
    update_time: localCache.updateTime,
    commit_id: localCache.commit_id,
  };
  return currentData;
}

export function snapshotBoard(userId?: number, useDefault = false) {
  const boardReady = useIsReady();

  if (!userId || useDefault) return DEFAULT_PROJECT_PREVIEW;
  const localCache = localCacheManager.data;
  if (!boardReady) return localCache.preview || DEFAULT_PROJECT_PREVIEW;

  const nodes = BoardStore.api.graph.allNodes.filter((node) => node.type === "image");
  const topThree = nodes.slice(Math.max(nodes.length - 3, 0)) as ImageNode[];
  const url = topThree.map((n) => n.renderParams.src).join(",");
  return url || DEFAULT_PROJECT_PREVIEW;
}
